import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';
import appInsightsService from '@/utils/appInsights';
import { isUnansweredRequestError } from '@/utils/infrastructure/communication/retryStrategy';

const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const RECHECK_INTERVAL_MS = 60 * 1000;

/**
 * TokenRefreshManager - Singleton class that manages token refresh logic.
 * The page owns the refresh timers; the Service Worker only gets a copy, because
 * the browser stops an idle worker after ~30s and its timers die with it.
 */
class TokenRefreshManager {
  constructor() {
    this.worker = null;

    // Map<sessionId, callback> — supports AUTOMY, PORTAL, and APP sessions concurrently.
    // Replaces the single this.currentSessionId + this.refreshCallback pair that caused
    // the last caller to overwrite all prior sessions in multi-session scenarios.
    this.refreshCallbacks = new Map();
    this.refreshTimers = new Map();
    this.isInitialized = false;

    // Last { token, expiresAt } per session. A worker update starts with empty
    // timers, so they are replayed into the new worker when it activates.
    this.sessionTokens = new Map();
    this.registration = null;

    // Per-session refresh guard — replaces the single isRefreshing boolean that
    // blocked all sessions when any one session was mid-refresh.
    this.refreshingSessions = new Set();

    // Guard so the visibilitychange/online listeners are only attached once,
    // regardless of how many init paths run (SW success / no SW / registration failure).
    this._wakeListenersAttached = false;

    // Kept for backward-compat: callers that use setRefreshCallback() without passing
    // a callback to startTokenRefresh() get this as the per-session default.
    this._pendingCallback = null;
  }

  /**
   * Initializes the token refresh manager and registers Service Worker
   * @returns {Promise<boolean>} Success status
   */
  async init() {
    if (this.worker) return true;
    this._ensureWakeListeners();
    if (!navigator.serviceWorker) {
      appInsightsService.trackException(
        new Error('Service Workers not supported'),
        {
          errorType: 'WORKER_NOT_SUPPORTED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: false,
          errorReason: 'Service Workers not available in this browser',
          fallbackUsed: true,
        },
        2, // Warning severity - expected in some browsers
      );

      this.isInitialized = true;
      return true;
    }

    try {
      const workerURL = '/tokenRefreshWorker.js';
      const registration = await navigator.serviceWorker.register(workerURL, { scope: '/' });
      this.registration = registration;
      registration.addEventListener('updatefound', this._handleWorkerUpdateFound);

      if (registration.active) {
        this.worker = registration.active;
      } else {
        // Wait for worker to activate with timeout
        const activationTimeout = 10000; // 10 seconds
        const activationPromise = new Promise((resolve, reject) => {
          const worker = registration.installing || registration.waiting;
          if (!worker) {
            reject(new Error('No worker available for activation'));
            return;
          }

          const timeoutId = setTimeout(() => {
            reject(new Error('Worker activation timeout'));
          }, activationTimeout);

          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') {
              clearTimeout(timeoutId);
              this.worker = worker;
              resolve();
            } else if (worker.state === 'redundant') {
              clearTimeout(timeoutId);
              reject(new Error('Worker became redundant during activation'));
            }
          });
        });

        await activationPromise;
      }

      // Setup message handler
      navigator.serviceWorker.addEventListener('message', this.handleWorkerMessage.bind(this));
      this.isInitialized = true;
      return true;
    } catch (error) {
      const isRegistrationError =
        error.message?.includes('register') || error.name === 'SecurityError';

      const isActivationError =
        error.message?.includes('activation') || error.message?.includes('timeout');

      appInsightsService.trackException(
        error,
        {
          errorType: 'WORKER_REGISTRATION_FAILED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: !!navigator.serviceWorker,
          errorReason: error.message || 'Unknown registration error',
          errorName: error.name,
          workerState: this.worker?.state || 'unknown',
          isRegistrationError,
          isActivationError,
          isRedundantError: error.message?.includes('redundant'),
          securityContext:
            error.name === 'SecurityError' ? 'Possible HTTPS or permissions issue' : undefined,
          fallbackUsed: true,
          workerURL: '/tokenRefreshWorker.js',
        },
        3,
      );

      this.isInitialized = true;
      return true;
    }
  }

  /**
   * Attaches the visibilitychange and online listeners exactly once. Safe to call
   * from any init path (SW active, SW unsupported, or registration failed).
   */
  _ensureWakeListeners() {
    if (this._wakeListenersAttached) return;
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    window.addEventListener('online', this._recheckSessions);
    this._wakeListenersAttached = true;
  }

  /**
   * The worker calls skipWaiting(), so a deploy (or another tab loading the new
   * worker) makes the current one redundant while this tab keeps running. Without
   * rebinding, every postMessage goes to a dead worker.
   * Listens on this registration (not controllerchange) because /apps/ pages are
   * controlled by the apps offline worker.
   */
  _handleWorkerUpdateFound = () => {
    const incoming = this.registration?.installing;
    if (!incoming) return;
    incoming.addEventListener('statechange', () => {
      if (incoming.state === 'activated') this._rebindWorker(incoming);
    });
  };

  _rebindWorker(worker) {
    if (!worker || worker === this.worker) return;
    this.worker = worker;
    this.sessionTokens.forEach(({ token, expiresAt }, sessionId) => {
      try {
        worker.postMessage({ type: 'START_REFRESH', payload: { sessionId, token, expiresAt } });
      } catch (_) {}
    });
  }

  _handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this._recheckSessions();
  };

  /**
   * Timers can fire late or not at all while the device sleeps, and a refresh
   * that failed offline only left the slow recheck behind. Re-arming from the
   * known expiry refreshes right away only when the token is inside the buffer.
   */
  _recheckSessions = () => {
    if (this.worker) {
      try {
        this.worker.postMessage({ type: 'RECHECK', payload: {} });
      } catch (_) {}
    }
    this.sessionTokens.forEach(({ expiresAt }, sessionId) => {
      if (this.refreshCallbacks.has(sessionId)) this.scheduleRefresh(sessionId, expiresAt);
    });
  };

  /**
   * Handles messages from Service Worker.
   * Looks up the callback by sessionId from the message payload so all active
   * sessions (AUTOMY, PORTAL, APP) are handled independently.
   */
  handleWorkerMessage = (event) => {
    const { type, payload } = event.data;
    if (type === 'REFRESH_TOKEN_REQUEST') {
      const { sessionId } = payload;
      if (this.refreshCallbacks.has(sessionId)) {
        this.executeTokenRefresh(sessionId);
      }
    }
  };

  /**
   * Executes token refresh and emits global events
   * @param {string} sessionId - Session identifier
   * @returns {Promise<boolean>} false when the callback failed with an answer from
   *   the server, so retrying with the same refresh token is pointless.
   */
  async executeTokenRefresh(sessionId) {
    if (this.refreshingSessions.has(sessionId)) return true;

    let retryable = true;
    const run = async () => {
      if (this.refreshingSessions.has(sessionId)) return;
      this.refreshingSessions.add(sessionId);
      try {
        const callback = this.refreshCallbacks.get(sessionId);
        if (callback) await callback(sessionId);
      } catch (error) {
        retryable = isUnansweredRequestError(error);
        tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESH_FAILED, { sessionId, error, retryable });
      } finally {
        this.refreshingSessions.delete(sessionId);
      }
    };

    // navigator.locks serializes refresh across all tabs for the same session.
    // When Tab A holds the lock, Tab B waits — preventing the multi-tab TOCTOU
    // race and the proactive/reactive dual-path race.
    // IE11 fallback: lock unavailable, falls through to in-process guard only.
    if (typeof navigator !== 'undefined' && navigator.locks) {
      await navigator.locks.request(`token-refresh-${sessionId}`, run);
    } else await run();
    return retryable;
  }

  /**
   * Sets a default callback used by the next startTokenRefresh call.
   * Preserved for callers (useTokenRefresh.js) that set the callback separately.
   * @param {Function} callback - Function to call for token refresh
   */
  setRefreshCallback(callback) {
    this._pendingCallback = callback;
  }

  /**
   * Starts monitoring token expiration for a session.
   * Stores the callback in the per-session Map so concurrent AUTOMY / PORTAL / APP
   * sessions don't overwrite each other.
   * @param {string} sessionId - Session identifier
   * @param {string} token - Access token
   * @param {number} expiresAt - Token expiration timestamp
   * @param {Function} [callback] - Optional per-call callback; falls back to _pendingCallback
   * @returns {Promise<boolean>} Success status
   */
  async startTokenRefresh(sessionId, token, expiresAt, callback) {
    // Associate the callback with this specific session.
    // Prefer an explicitly-passed callback; fall back to the one set via setRefreshCallback().
    const resolvedCallback = callback || this._pendingCallback;
    if (resolvedCallback) this.refreshCallbacks.set(sessionId, resolvedCallback);
    this.sessionTokens.set(sessionId, { token, expiresAt });
    this.scheduleRefresh(sessionId, expiresAt);
    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'START_REFRESH',
          payload: { sessionId, token, expiresAt },
        });
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'START_REFRESH',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker communication failure',
          },
          2,
        );
      }
    }
    return true;
  }

  /**
   * Arms the session's page timer to refresh REFRESH_BUFFER_MS before expiry,
   * or right away when the token is already inside that window.
   * @param {string} sessionId - Session identifier
   * @param {number} expiresAt - Token expiration timestamp
   */
  scheduleRefresh(sessionId, expiresAt) {
    this._armRefreshTimer(sessionId, Math.max(0, expiresAt - Date.now() - REFRESH_BUFFER_MS));
  }

  _armRefreshTimer(sessionId, delayMs) {
    const existing = this.refreshTimers.get(sessionId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(async () => {
      const retryable = await this.executeTokenRefresh(sessionId);
      // A successful refresh re-arms through updateToken. If this timer is still
      // the latest, the refresh was skipped (another tab got there first) or
      // never reached the server, and nothing else would schedule the next one.
      if (
        !retryable ||
        this.refreshTimers.get(sessionId) !== timer ||
        !this.refreshCallbacks.has(sessionId)
      ) {
        return;
      }
      this._armRefreshTimer(sessionId, RECHECK_INTERVAL_MS);
    }, delayMs);
    this.refreshTimers.set(sessionId, timer);
  }

  /**
   * Updates token information after successful refresh.
   * No longer guards on a single currentSessionId — any active session is valid.
   * @param {string} sessionId - Session identifier
   * @param {string} token - New access token
   * @param {number} expiresAt - New expiration timestamp
   */
  async updateToken(sessionId, token, expiresAt) {
    if (this.sessionTokens.has(sessionId)) this.sessionTokens.set(sessionId, { token, expiresAt });
    // Emit global event that token was refreshed
    tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESHED, {
      sessionId,
      token,
      expiresAt,
      timestamp: Date.now(),
    });

    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'UPDATE_TOKEN',
          payload: { sessionId, token, expiresAt },
        });
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'UPDATE_TOKEN',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker token update failure',
          },
          2,
        );
      }
    }
    if (this.refreshCallbacks.has(sessionId)) this.scheduleRefresh(sessionId, expiresAt);
  }

  /**
   * Notifies that a socket reconnection is needed
   * @param {string} reason - Reason for reconnection
   */
  requestSocketReconnect(reason = 'token_refresh') {
    // Emit with all active session IDs so listeners can correlate correctly.
    const sessionIds = Array.from(this.refreshCallbacks.keys());
    tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, {
      reason,
      timestamp: Date.now(),
      sessionIds,
      // Keep singular sessionId for backward-compat listeners that expect it.
      sessionId: sessionIds[0] ?? null,
    });
  }

  /**
   * Stops token refresh monitoring for a specific session and cleans up its resources.
   * @param {string} sessionId - Session identifier to stop
   */
  async stopTokenRefresh(sessionId) {
    if (!sessionId) {
      // Legacy call-site in useTokenRefresh cleanup passes no argument — stop all sessions.
      const sessionIds = Array.from(this.refreshCallbacks.keys());
      await Promise.all(sessionIds.map((id) => this.stopTokenRefresh(id)));
      this._pendingCallback = null;
      this.refreshingSessions.clear();
      return;
    }

    this.refreshCallbacks.delete(sessionId);
    this.sessionTokens.delete(sessionId);
    const timer = this.refreshTimers.get(sessionId);

    if (timer) {
      clearTimeout(timer);
      this.refreshTimers.delete(sessionId);
    }

    if (this.worker) {
      try {
        this.worker.postMessage({ type: 'STOP_REFRESH', payload: { sessionId } });
      } catch (error) {}
    }
  }
}

export default new TokenRefreshManager();
